import throttle from 'lodash/throttle';
import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Platform, StyleSheet, View } from 'react-native';
import { useMediaQuery } from 'react-responsive';

import {
  useConsumerQuery,
  useUpdateConsumerMutation,
} from 'apollo/queries/autogenerate/hooks';
import Button from 'shared/modules/common/components/atoms/Button';
import DatePicker from 'shared/modules/common/components/molecules/DatePicker';
import validator from 'shared/utils/validator';

type Props = {
  /**
   * Invoked when editing is successful
   */
  onSuccess?: () => void;
};

const EditUserDateOfBirthForm = (props: Props) => {
  const isBigScreen = useMediaQuery({ minWidth: 768 });

  const { refetch, loading } = useConsumerQuery({
    fetchPolicy: 'network-only',
    skip: true,
  });

  const [updateConsumerMutation] = useUpdateConsumerMutation({
    update(cache) {
      refetch({
        masked: true,
      }).then(({ data }) => {
        cache.modify({
          fields: {
            consumer(cachedData) {
              return {
                ...cachedData,
                ...data?.consumer,
              };
            },
          },
        });
      });
    },
  });

  const form = useFormWithValidations({
    dateOfBirth: '',
  });

  useEffect(() => {
    refetch({
      masked: false,
    }).then(({ data }) => {
      // We set the default values here
      // So any changes afterwards will be treated "dirty" and can be handled appropriately
      form.reset({ dateOfBirth: data.consumer.dateOfBirth });
    });
  }, []);

  const submit = useCallback(
    throttle(
      async () => {
        try {
          await updateConsumerMutation({
            variables: {
              dateOfBirth: form.getValues('dateOfBirth'),
            },
          });

          props.onSuccess?.();
        } catch (err) {
          /* noop */
        }
      },
      2000,
      { leading: true, trailing: false }
    ),
    []
  );

  return (
    <View
      style={style.wrapper}
      {...Platform.select({ web: { accessibilityRole: 'form' } })}
    >
      <View style={style.fieldWrapper}>
        <DatePicker
          placeholder="YYYY-MM-DD"
          value={form.getValues('dateOfBirth')}
          label="生年月日"
          selectButtonText="保存する"
          onSelect={(text) => form.setValue('dateOfBirth', text)}
          onBlur={() => form.triggerValidation('dateOfBirth')}
          message={(form.errors.dateOfBirth?.message as string) || ''}
          state={form.errors.dateOfBirth?.message ? 'error' : undefined}
          editable={!loading}
          style={style.textInput}
          dialogStyle={style.pickerDialog}
          showAsDialog={isBigScreen}
          id="edit-user-birthday-picker"
        />
      </View>
      <Button
        title="次へ"
        onPress={async () => {
          const isValid = await form.triggerValidation();

          isValid && submit();
        }}
        disabled={!form.formState.dirty}
        id="edit-user-birthday-submit-button"
      />
    </View>
  );
};

const useFormWithValidations = (props: { dateOfBirth: string }) => {
  const form = useForm<typeof props>({
    defaultValues: props,
  });

  useEffect(() => {
    form.register(
      {
        name: 'dateOfBirth',
      },
      {
        validate: validator.getRules([
          'mustNotBeEmptyOrSpace',
          'mustBeValidDate',
          'mustBeInValidDateOfBirthRange',
        ]),
      }
    );
  }, [form.register]);

  form.watch();

  return form;
};

const style = StyleSheet.create({
  wrapper: {
    flex: 1,
    justifyContent: 'space-between',
  },
  fieldWrapper: {
    marginBottom: 24,
  },
  textInput: {
    marginBottom: 24,
  },
  pickerDialog: {
    width: 424,
  },
});

export default EditUserDateOfBirthForm;
